import { useQuery } from '@apollo/client';
import { Box, Fab, Stack } from '@mui/material';
import { List, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import dayjs from 'dayjs';
import { type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetRewardsQuery, GetRewardsQueryVariables } from '../client/generated/graphql';
import { getRewardsQuery } from '../client/queries/rewards';
import { CommonIcon } from '../components/common/CommonIcon';
import { Page } from '../components/Page';
import { RewardDisplay } from '../components/RewardDisplay';
import { RewardsPageSkeleton } from '../components/rewards/skeleton/RewardsPageSkeleton';
import { rewardCreatePath } from '../navigation/paths';

export const RewardsPage: FC = () => {
  const navigate = useNavigate();
  const { loading, data } = useQuery<GetRewardsQuery, GetRewardsQueryVariables>(getRewardsQuery);

  const handleCreateReward = () => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    navigate(rewardCreatePath);
  };

  if (loading || !data) return <RewardsPageSkeleton />;

  const { rewards, user } = data;
  const collectedCurrency = user.collectedCurrency;
  
  const availableRewards = rewards.filter(r => !r.isRewarded);
  const purchasedRewards = rewards
    .filter(r => r.isRewarded)
    .sort((a, b) => {
      if (!a.rewardedOn || !b.rewardedOn) return 0;
      return dayjs(b.rewardedOn).isAfter(dayjs(a.rewardedOn)) ? 1 : -1;
    });

  return (
    <>
      <Page back insetEnabled={false}>
        <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
          <CommonIcon icon='paid' type="filled" size={28} color="var(--tg-theme-button-color)" />
          <Text weight='2'>{collectedCurrency}</Text>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', paddingBottom: '20px' }}>
          <List style={{ margin: '8px 0' }}>
            {availableRewards.map(reward => (
              <RewardDisplay
                key={reward.id}
                reward={reward}
                collectedCurrency={collectedCurrency}
              />
            ))}
          </List>

          {purchasedRewards.length > 0 && (
            <>
              <Stack direction="row" justifyContent="center">
                <Text weight="2" style={{ fontSize: '20px' }}>
                  Purchased
                </Text>
              </Stack>
              <List style={{ margin: '8px 0' }}>
                {purchasedRewards.map(reward => (
                  <RewardDisplay
                    key={reward.id}
                    reward={reward}
                    collectedCurrency={collectedCurrency}
                  />
                ))}
              </List>
            </>
          )}
        </Box>
      </Page>
      
      <Fab
        variant="extended"
        color="primary" 
        onClick={handleCreateReward}
        sx={{ 
          position: 'fixed', 
          bottom: 90, 
          right: 10,
          zIndex: 1000,
          textTransform: 'none'
        }}
      >
        <CommonIcon icon="paid" size={26} style={{ marginRight: '4px' }} color='var(--tg-theme-button-text-color)' type="filled" />
        <Text weight='2' style={{ fontSize: '16px', color: 'var(--tg-theme-button-text-color)' }}>New reward</Text>
      </Fab>
    </>
  );
}; 